/** What a person is told when the address they typed is not an email address. */
export const EMAIL_INVALID_MESSAGE = 'Enter a valid email.'

export type EmailAddressCheck = { ok: true; email: string } | { ok: false; message: string }

/** RFC 5321 section 4.5.3.1.3 caps a whole address at 254 characters. */
const MAX_ADDRESS_LENGTH = 254

/** RFC 5321 section 4.5.3.1.1 caps the local part, before the `@`, at 64 characters. */
const MAX_LOCAL_LENGTH = 64

/**
 * The local part: no whitespace, no second `@`, none of the specials that need quoting. Dots sit between atoms
 * (RFC 5321 section 4.1.2), so there is none at either end and never two in a row.
 */
const LOCAL_PART = /^[^\s@,;:<>()[\]\\".]+(\.[^\s@,;:<>()[\]\\".]+)*$/

/**
 * The domain: at least two dot-separated labels, none empty, with no whitespace, none of `@ / ? # % \`, and none of
 * the specials the local part refuses (`, ; : < > ( ) [ ] "`). No hostname holds one of those: `founder@company.co/uk`
 * is a typo to catch rather than a host and a path, and `founder@gmail.com>` or `founder@gmail.com,` is a paste slip.
 */
const DOMAIN = /^[^\s@./?#%\\,;:<>()[\]"]+(\.[^\s@./?#%\\,;:<>()[\]"]+)+$/

/** A control or invisible formatting character anywhere: no mailbox needs one, and the person cannot see it. */
const INVISIBLE = /[\p{Cc}\p{Cf}]/u

/**
 * Checks an email address by its syntax alone and returns it trimmed (#1721). Any domain will do, personal ones
 * included: there is no allow or deny list and no DNS lookup, because the code emailed to it proves the address.
 * The sign-in screen and the API both use this, so the two never disagree about what counts as an address.
 */
export function checkEmailAddress(raw: string): EmailAddressCheck {
    const email = raw.trim()
    return isEmailAddress(email) ? { ok: true, email } : { ok: false, message: EMAIL_INVALID_MESSAGE }
}

function isEmailAddress(email: string): boolean {
    if (email.length > MAX_ADDRESS_LENGTH || INVISIBLE.test(email)) {
        return false
    }
    const at = email.indexOf('@')
    if (at < 1 || at > MAX_LOCAL_LENGTH) {
        return false
    }
    return LOCAL_PART.test(email.slice(0, at)) && DOMAIN.test(email.slice(at + 1))
}
